import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToHash } from "@/lib/scrollTo";
import { useLanguage } from "@/lib/language";

const EASE = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative h-[100svh] min-h-[620px] overflow-hidden bg-charcoal"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.12]">
        <motion.img
          src="/images/salon.jpg"
          alt={t.hero.heroAlt}
          initial={{ scale: 1.14 }}
          animate={{ scale: 1 }}
          transition={{ duration: 3, ease: EASE }}
          className="h-full w-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/20 to-charcoal/80" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-end pb-20 lg:pb-24"
      >
        <motion.p
          initial={{ opacity: 0, y: 14, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 1 }}
          className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-cream/95 mb-6"
          style={{ textShadow: "0 1px 14px rgba(26,24,23,0.65)" }}
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <motion.h1
          data-testid="hero-title"
          initial={{ opacity: 0, y: 56, filter: "blur(14px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.6, delay: 0.35, ease: EASE }}
          className="mt-2"
        >
          <img
            src="/images/logo-real-white.png"
            alt={t.brand.name}
            className="h-36 sm:h-52 lg:h-64 w-auto drop-shadow-[0_12px_36px_rgba(26,24,23,0.5)]"
          />
        </motion.h1>

        <motion.div
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 1.2, delay: 0.85, ease: EASE }}
          className="h-px bg-gold mt-7"
        />

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 1.05, ease: EASE }}
          className="mt-6 max-w-xl text-cream/95 text-base sm:text-lg font-light leading-relaxed"
          style={{ textShadow: "0 1px 14px rgba(26,24,23,0.55)" }}
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 1.25, ease: EASE }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-cta-button"
            onClick={() => scrollToHash("#przestrzenie")}
            className="px-8 py-4 bg-gold text-charcoal text-xs uppercase tracking-[0.2em] font-medium rounded-full hover:bg-goldDark hover:-translate-y-0.5 transition-all duration-300"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollToHash("#o-rezydencji")}
            className="px-8 py-4 border border-cream/40 text-cream text-xs uppercase tracking-[0.2em] rounded-full hover:bg-cream/10 hover:-translate-y-0.5 transition-all duration-300"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1.2 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 hidden sm:flex flex-col items-center gap-3"
        aria-hidden="true"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-cream/60 [writing-mode:vertical-rl]">
          {t.hero.scroll}
        </span>
        <span className="relative w-px h-14 bg-cream/20 overflow-hidden">
          <span className="absolute top-0 left-0 w-px h-5 bg-gold animate-scrollDot" />
        </span>
      </motion.div>
    </section>
  );
};

export default Hero;
